import { Link } from "react-router-dom";
import { useState } from "react";
import AccordionItem from "../components/AccordionItem";
import SEO from "../components/SEO";
import CourseCard from "../components/CourseCard";
import TestimonialCard from "../components/TestimonialCard";
import LoadingSpinner from "../components/LoadingSpinner";
import { useData } from "../hooks/useData";
import { getCourseEnrollUrl } from "../utils/constants";
import MediaSlider from "../components/MediaSlider";

const Home = () => {
  const { data: courses, loading: coursesLoading } = useData("courses.json");
  const { data: testimonials, loading: testimonialsLoading } =
    useData("testimonials.json");
  const { data: faqs, loading: faqsLoading } = useData("faqs.json");
  const { data: siteData, loading: siteLoading } = useData("site.json");

  const featuredCourses = courses?.filter((course) => course.featured) || [];
  const [showAllTestimonials, setShowAllTestimonials] = useState(false);

  return (
    <>
      <SEO />

      {/* Hero Section */}
      <section className="gradient-bg text-white py-8 sm:py-16 lg:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 items-center gap-8 lg:gap-6">
            {/* Left: Text (span 2 cols) */}
            <div className="lg:col-span-2 animate-fade-in text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Transform Your English.
                <span className="block text-yellow-400">
                  Transform Your Future.
                </span>
              </h1>

              <p className="text-lg sm:text-xl md:text-2xl mb-8 text-gray-200">
                Transform your career with our comprehensive English courses.
                Join thousands of successful students worldwide.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href="#courses"
                  className="bg-transparent border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-200"
                >
                  Browse Courses
                </a>
                <Link
                  to="/test"
                  className="bg-yellow-400 text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-yellow-300 transition-colors duration-200"
                >
                  Take Free Level Test
                </Link>
                <a
                  href={getCourseEnrollUrl("Demo Session")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-secondary-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-secondary-400 transition-colors duration-200"
                >
                  Book 1:1 Demo @ ₹99
                </a>
              </div>
            </div>

            {/* Right: Video (1 col) */}
            <div className="lg:col-span-1 animate-slide-up flex justify-center lg:justify-end">
              <div className="w-full max-w-[280px] sm:max-w-[360px] md:max-w-[400px] lg:max-w-[320px] aspect-[9/16] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20">
                <video
                  src="media/English Tunnels.mp4"
                  loop
                  autoPlay
                  muted
                  playsInline
                  controls
                  poster="media/videothumb.webp"
                  className="w-full h-full object-contain object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Courses Section */}
      <section id="courses" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Featured Courses
            </h2>
            <p className="text-xl text-gray-600">
              Discover our most popular English courses
            </p>
          </div>

          {coursesLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3  gap-10 ">
              {featuredCourses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why choose us Section*/}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Why Choose Us?
            </h2>
            <p className="text-base sm:text-xl text-gray-600">
              Experience the difference with our proven teaching methodology
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 sm:gap-8">
            {siteData?.benefits?.map((benefit, index) => (
              <article
                key={index}
                className="group rounded-xl bg-white shadow-sm ring-1 ring-gray-200 hover:shadow-md transition-shadow duration-200 p-4 sm:p-6"
              >
                {/* Mobile: left-aligned, Desktop: center icon */}
                <div className="flex items-start sm:block">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-primary-50 ring-1 ring-primary-100 grid place-items-center">
                      <div
                        className="text-primary-600"
                        // your SVG string
                        dangerouslySetInnerHTML={{ __html: benefit.icon }}
                      />
                    </div>
                  </div>

                  {/* Text */}
                  <div className="ml-4 sm:ml-0 mt-0 sm:mt-4">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                      {benefit.title}
                    </h3>
                    <p className=" sm:mt-2 text-sm sm:text-base text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>

                {/* Subtle underline accent on hover (desktop) */}
                {/* <span className="mt-4 block h-0.5 w-0 bg-primary-200 group-hover:w-full transition-all duration-300 rounded-full" /> */}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              How It Works
            </h2>
            <p className="text-base sm:text-xl text-gray-600">
              Start your English learning journey in just 4 simple steps
            </p>
          </div>

          {/** Mobile: vertical timeline **/}
          <div className="md:hidden max-w-md mx-auto">
            {[
              {
                n: 1,
                title: "Take Level Test",
                desc: "Complete our free assessment to determine your current English level",
              },
              {
                n: 2,
                title: "Choose Course",
                desc: "Select the perfect course based on your level and learning goals",
              },
              {
                n: 3,
                title: "Start Learning",
                desc: "Begin your personalized learning journey with expert guidance",
              },
              {
                n: 4,
                title: "Get Certificate",
                desc: "Receive your official certificate to showcase your achievement",
              },
            ].map((s, i, arr) => (
              <div key={s.n} className="relative pl-12 pb-8 last:pb-0">
                {/* vertical line */}
                {i < arr.length - 1 && (
                  <span className="absolute left-6 top-10 bottom-0 w-0.5 bg-primary-200" />
                )}
                {/* number bubble */}
                <span className="absolute left-0 top-1.5 flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white text-base font-bold">
                  {s.n}
                </span>

                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {s.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>

          {/** Desktop/Tablet: 4-card grid (your original look) **/}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                n: 1,
                title: "Take Level Test",
                desc: "Complete our free assessment to determine your current English level",
              },
              {
                n: 2,
                title: "Choose Course",
                desc: "Select the perfect course based on your level and learning goals",
              },
              {
                n: 3,
                title: "Start Learning",
                desc: "Begin your personalized learning journey with expert guidance",
              },
              {
                n: 4,
                title: "Get Certificate",
                desc: "Receive your official certificate to showcase your achievement",
              },
            ].map((s) => (
              <div key={s.n} className="text-center">
                <div className="bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {s.n}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {s.title}
                </h3>
                <p className="text-gray-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certificate Showcase Section */}
      <section className="py-20 bg-gradient-to-r from-primary-700 via-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Certificate image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 transform transition hover:scale-105">
                <img
                  src="/media/cert.webp"
                  alt="English Tunnels Certificate of Completion"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Right: Content */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Get Certified & Showcase Your Skills
              </h2>
              <p className="text-lg text-gray-100 mb-8">
                Earn a shareable, verifiable certificate to boost your resume
                and profile.
              </p>

              <ul className="space-y-4 text-gray-100 mb-10">
                <li className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-secondary-400/30 text-secondary-200">
                    ✓
                  </span>
                  <span>Recognized by top companies and institutions.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-secondary-400/30 text-secondary-200">
                    ↓
                  </span>
                  <span>
                    Get your certificate in PDF format instantly after
                    completion.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-secondary-400/30 text-secondary-200">
                    ⤴
                  </span>
                  <span>Share on LinkedIn and add to your resume.</span>
                </li>
              </ul>

              {/* <div className="flex flex-wrap gap-4">
                <a
                  href={getWhatsAppUrl(
                    "Hi! I want to know about your certificates."
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-lg bg-secondary-500 px-6 py-3 font-semibold text-white hover:bg-secondary-400 transition"
                >
                  Get Started
                </a>
                <a
                  href="/media/cert.png"
                  download
                  className="inline-flex items-center justify-center rounded-lg border border-white/20 px-6 py-3 font-semibold hover:bg-white/10 transition"
                >
                  Preview Sample
                </a>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-10 sm:mb-14 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">
              What Our Students Say
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              Real success stories from our community
            </p>
          </div>

          {testimonialsLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              {/* Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 items-start">
                {(testimonials || [])
                  .slice(
                    0,
                    showAllTestimonials ? (testimonials || []).length : 3,
                  )
                  .map((t) => (
                    <TestimonialCard key={t.id} testimonial={t} />
                  ))}
              </div>

              {/* Show More / Show Less */}
              {(testimonials?.length || 0) > 3 && (
                <div className="mt-8 sm:mt-10 flex justify-center">
                  <button
                    onClick={() => setShowAllTestimonials((v) => !v)}
                    className="w-full sm:w-auto px-5 sm:px-6 py-3 rounded-lg font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                    aria-expanded={showAllTestimonials}
                  >
                    {showAllTestimonials ? "Show Less" : "Show More"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Media Slider Section */}
      {/* <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Student Highlights
            </h2>
            <p className="text-xl text-gray-600">
              Photos and videos from our learners
            </p>
          </div>

          <MediaSlider
            items={[
              {
                type: "video",
                src: "/media/vid.mp4",
                poster: "/media/ss.png",
              },
              {
                type: "image",
                src: "/media/s1.jpg",
                alt: "Riya presenting in class",
              },
              {
                type: "image",
                src: "/media/s2.jpg",
                poster: "",
              },
              {
                type: "image",
                src: "/media/s3.jpg",
                alt: "Group activity",
              },

              {
                type: "image",
                src: "/media/s4.jpg",
                alt: "Certificate day",
              },
              {
                type: "image",
                src: "/media/s5.jpg",
                alt: "Certificate day",
              },
              {
                type: "image",
                src: "/media/s6.jpg",
                alt: "Certificate day",
              },
            ]}
            autoPlay
            interval={4500}
          />
        </div>
      </section> */}

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              Find answers to common questions about our courses
            </p>
          </div>

          {faqsLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {faqs?.map((faq, i) => (
                <AccordionItem
                  key={faq.id}
                  id={faq.id}
                  question={faq.question}
                  answer={faq.answer}
                  defaultOpen={i === 0} // open first by default (optional)
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Your English Journey?
          </h2>
          <p className="text-xl mb-8">
            Join thousands of students who have transformed their careers with
            our courses
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/test"
              className="bg-white text-primary-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Take Free Level Test
            </Link>
            <a
              href={getCourseEnrollUrl("General English Course")}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200"
            >
              Contact Us Now
            </a>
            <a
              href={getCourseEnrollUrl("Hi, I want to book demo ")}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-secondary-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-secondary-400 transition-colors duration-200"
            >
              Book Demo @ ₹99
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
